import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  AlertCircle,
  Camera,
  Check,
  Gift,
  Loader2,
  Package,
  PenLine,
  RotateCcw,
  ShieldCheck,
  Sparkles,
  Tag,
  Target,
  Trash2,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { Offer } from "@shared/schema";
import { personaNameTier, PERSONA_NAME_TIER_COMPACT } from "@/components/persona-build-card";

// ── Build-in-progress + failed cards for Build-with-AI offerings ────────────
// Rendered by the Offerings grid while a row has buildStatus 'building' /
// 'failed' (persist-first generation — see server/services/offer-sale-build).
// Mirrors the persona build card's design language (same footprint, dark
// band, staged narration, progress, step timeline) themed for offerings, and
// reuses the absorb-into-a-card motif language of the old inline
// OfferingBuildAnimation. Kept in deliberate lockstep with
// sale-build-card.tsx — change both together.
//
// Unlike personas there is no per-step server progress for the CONTENT pass:
// the offering build is ONE LLM pass, so that staging is time-based theater
// (exactly what the old inline animation was), capped below 100% until the
// row really lands. The build lifecycle now extends through the AI hero
// photo, and the server signals that phase by clearing buildInput (and
// re-stamping buildStartedAt as the hero-phase clock) — so the final
// "Hero imagery" step keys off buildInput == null, NOT elapsed time, and the
// card must never rewind when the clock restarts.

const SERIF = { fontFamily: `'Iowan Old Style', 'Palatino Linotype', Georgia, 'Times New Roman', serif` };
const BAND = "#231c16";
const CARD_MIN_H = "min-h-[480px]";

type StepKey = "positioning" | "structure" | "proof" | "creative" | "imagery";

// Time-based stage boundaries (seconds since the build started). "creative"
// keeps "working" until the server clears buildInput (content landed), which
// flips the card into the flag-driven "imagery" step — that one is never
// reached by time alone.
const STEPS: { key: StepKey; label: string; icon: typeof PenLine; until: number; narration: string }[] = [
  { key: "positioning", label: "Positioning", icon: Target, until: 9, narration: "Sharpening the value proposition…" },
  { key: "structure", label: "Offer structure", icon: Package, until: 22, narration: "Structuring what's included & pricing…" },
  { key: "proof", label: "Proof & objections", icon: ShieldCheck, until: 38, narration: "Anticipating objections & lining up proof…" },
  { key: "creative", label: "Creative angles", icon: Sparkles, until: Infinity, narration: "Writing hooks & creative angles…" },
  { key: "imagery", label: "Hero imagery", icon: Camera, until: Infinity, narration: "Staging the hero photograph…" },
];

/** Seconds elapsed since the row's buildStartedAt (falls back to mount). */
function useBuildElapsed(startedAt: string | Date | null | undefined): number {
  const mountedAtRef = useRef(Date.now());
  const startMs = (() => {
    if (!startedAt) return mountedAtRef.current;
    const t = new Date(startedAt as any).getTime();
    return isNaN(t) ? mountedAtRef.current : Math.min(t, Date.now());
  })();
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  return Math.max(0, (now - startMs) / 1000);
}

/** Eased time-based percent, capped at 90 until the row actually completes. */
function timedPercent(elapsed: number): number {
  return Math.min(90, Math.round(90 * (1 - Math.exp(-elapsed / 38))));
}

function activeStep(elapsed: number): StepKey {
  for (const s of STEPS) if (elapsed < s.until) return s.key;
  return "creative";
}

/** Types the narration once per step; the caret keeps blinking after. */
function Typewriter({ text }: { text: string }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    setN(0);
    const id = window.setInterval(() => {
      setN((v) => (v >= text.length ? v : v + 1));
    }, 42);
    return () => window.clearInterval(id);
  }, [text]);
  return (
    <span>
      {text.slice(0, n)}
      <span className="pb-caret inline-block w-[6px] h-[12px] translate-y-[1.5px] bg-white/60" />
    </span>
  );
}

/** Positioning: target + manuscript lines writing themselves in a loop. */
function PositioningStage() {
  return (
    <div className="flex flex-col items-start gap-2.5 w-[110px]">
      <Target className="h-5 w-5 text-white/35 mb-0.5" />
      {[100, 82, 92, 64].map((w, i) => (
        <span
          key={i}
          className="pb-write-line h-1.5 rounded-full bg-white/20 origin-left"
          style={{ width: `${w}%`, animationDelay: `${i * 0.55}s` }}
        />
      ))}
    </div>
  );
}

/** Offer structure: component slots popping into a bundle, one per beat. */
function StructureStage({ elapsed }: { elapsed: number }) {
  const icons = [Package, Gift, Tag, ShieldCheck, Sparkles, Target];
  // Fill the six slots across this stage's window (9s → 22s).
  const filled = Math.max(1, Math.min(6, Math.floor((elapsed - 9) / 2) + 1));
  return (
    <div className="grid grid-cols-3 gap-2.5 place-items-center">
      {icons.map((Icon, i) =>
        i < filled ? (
          <span
            key={`filled-${i}`}
            className="pb-pop-in h-9 w-9 rounded-xl bg-emerald-500/25 border border-emerald-400/40 flex items-center justify-center"
          >
            <Icon className="h-4 w-4 text-emerald-300" />
          </span>
        ) : (
          <span
            key={`empty-${i}`}
            className={`h-9 w-9 rounded-xl border ${
              i === filled ? "border-white/40 bg-white/10 animate-pulse" : "border-dashed border-white/15"
            }`}
          />
        ),
      )}
    </div>
  );
}

/** Proof & objections: shield / check / quote icons breathing. */
function ProofStage() {
  const items = [
    { icon: ShieldCheck, tint: "text-emerald-300/80" },
    { icon: AlertCircle, tint: "text-rose-300/80" },
    { icon: Check, tint: "text-amber-300/80" },
  ];
  return (
    <div className="flex items-center gap-3">
      {items.map(({ icon: Icon, tint }, i) => (
        <span
          key={i}
          className="pb-breathe h-10 w-10 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center"
          style={{ animationDelay: `${i * 0.4}s` }}
        >
          <Icon className={`h-[18px] w-[18px] ${tint}`} />
        </span>
      ))}
    </div>
  );
}

/** Hero imagery: a camera core with twinkling accents (hero photo phase). */
function ImageryStage() {
  return (
    <div className="relative h-[96px] w-[96px] flex items-center justify-center">
      <span className="pb-breathe h-14 w-14 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center">
        <Camera className="h-6 w-6 text-sky-200/80" />
      </span>
      {[
        { top: "4px", left: "10px", d: "0s" },
        { top: "12px", right: "6px", d: "0.7s" },
        { bottom: "8px", left: "4px", d: "1.3s" },
        { bottom: "2px", right: "14px", d: "1.9s" },
      ].map((p, i) => (
        <span
          key={i}
          className="ob-twinkle absolute h-1.5 w-1.5 rounded-full bg-sky-200/70"
          style={{ ...p, animationDelay: (p as any).d } as any}
        />
      ))}
    </div>
  );
}

/** Creative angles: a sparkle core with twinkling accents (finishing pass). */
function CreativeStage() {
  return (
    <div className="relative h-[96px] w-[96px] flex items-center justify-center">
      <span className="pb-breathe h-14 w-14 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center">
        <Sparkles className="h-6 w-6 text-amber-200/80" />
      </span>
      {[
        { top: "4px", left: "10px", d: "0s" },
        { top: "12px", right: "6px", d: "0.7s" },
        { bottom: "8px", left: "4px", d: "1.3s" },
        { bottom: "2px", right: "14px", d: "1.9s" },
      ].map((p, i) => (
        <span
          key={i}
          className="ob-twinkle absolute h-1.5 w-1.5 rounded-full bg-amber-200/70"
          style={{ ...p, animationDelay: (p as any).d } as any}
        />
      ))}
    </div>
  );
}

// ── Building card ────────────────────────────────────────────────────────────

/**
 * Dedicated build-in-progress offering card: staged, time-based narration
 * with step-specific animations. Only ever shows build scaffolding — no
 * hero, chips, or quote leak through before the build resolves.
 */
export function OfferingBuildCard({ offer, index }: { offer: Offer; index: number }) {
  const elapsed = useBuildElapsed(offer.buildStartedAt as any);
  // Content landed ⇢ hero phase: the server clears buildInput and restarts
  // buildStartedAt, so this flag (not time) drives the final step and the
  // progress bar must not rewind.
  const heroPhase = offer.buildInput == null;
  const active: StepKey = heroPhase ? "imagery" : activeStep(elapsed);
  const step = STEPS.find((s) => s.key === active)!;
  const pct = heroPhase
    ? Math.min(97, 90 + Math.round(7 * (1 - Math.exp(-elapsed / 45))))
    : timedPercent(elapsed);

  return (
    <Card
      className={`persona-card relative overflow-hidden rounded-2xl h-full ${CARD_MIN_H} border-border/40 cursor-default`}
      style={{ animationDelay: `${index * 80}ms` }}
      data-testid={`card-offer-generating-${offer.id}`}
    >
      <CardContent className="p-0 flex flex-col h-full">
        {/* ── Dark band: name + live narration + step stage ─────────── */}
        <div
          className="relative overflow-hidden h-[200px] md:h-[210px] flex-shrink-0 flex flex-col justify-center px-5 py-5"
          style={{ backgroundColor: BAND }}
        >
          <div className="absolute inset-y-0 right-0 w-[45%] bg-gradient-to-l from-white/[0.05] to-transparent flex items-center justify-center pointer-events-none">
            {/* One stage at a time, keyed so it cross-fades on step change. */}
            <div key={active} className="pb-fade-slide">
              {active === "positioning" ? (
                <PositioningStage />
              ) : active === "structure" ? (
                <StructureStage elapsed={elapsed} />
              ) : active === "proof" ? (
                <ProofStage />
              ) : active === "imagery" ? (
                <ImageryStage />
              ) : (
                <CreativeStage />
              )}
            </div>
          </div>
          <div className="relative z-10 max-w-[58%] flex flex-col gap-2.5 overflow-hidden">
            <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-white/10 border border-white/15 px-2.5 py-0.5 text-[8.5px] font-semibold uppercase tracking-[0.18em] text-white/70">
              <Tag className="h-3 w-3" />Offering
            </span>
            {/* Full name always shown — type steps down for long names. */}
            <h3
              className={`${PERSONA_NAME_TIER_COMPACT[personaNameTier(offer.name)]} text-[#f3ede3]`}
              style={SERIF}
              data-testid={`text-name-${offer.id}`}
            >
              {offer.name}
            </h3>
            {/* Fixed-height narration slot: copy swaps without layout shift. */}
            <div className="h-[36px]">
              <p
                key={active}
                className="pb-fade-slide text-[12px] leading-[18px] text-white/65 line-clamp-2"
                data-testid={`build-narration-${offer.id}`}
              >
                {active === "positioning" ? <Typewriter text={step.narration} /> : step.narration}
              </p>
            </div>
          </div>
        </div>

        {/* ── Overall progress (time-based, capped until the row lands) ── */}
        <div className="px-5 pt-4" data-testid={`build-progress-bar-${offer.id}`}>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[9px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              Building offering
            </span>
            <span className="text-[10.5px] font-semibold tabular-nums text-muted-foreground">{pct}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full bg-primary transition-all duration-700 ease-out"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        {/* ── Step timeline ─────────────────────────────────────────── */}
        <div className="px-5 pt-3.5 space-y-2" data-testid={`build-progress-${offer.id}`}>
          {STEPS.map((s) => {
            const Icon = s.icon;
            // In the hero phase every content step is done regardless of the
            // (restarted) clock; before it, time decides as always.
            const done = heroPhase ? s.key !== "imagery" : elapsed >= s.until;
            const isActive = active === s.key;
            return (
              <div
                key={s.key}
                className="flex items-center gap-2 text-[11.5px] leading-[18px]"
                data-testid={`build-step-${s.key}-${offer.id}`}
              >
                <span className="h-4 w-4 flex items-center justify-center flex-shrink-0">
                  {done ? (
                    <span key="done" className="pb-check-in flex items-center justify-center h-4 w-4 rounded-full bg-emerald-500/15">
                      <Check className="h-2.5 w-2.5 text-emerald-500" strokeWidth={3} />
                    </span>
                  ) : isActive ? (
                    <span className="relative flex items-center justify-center h-4 w-4">
                      <span className="absolute h-3.5 w-3.5 rounded-full bg-primary/20 animate-ping" />
                      <Icon className="relative h-3 w-3 text-primary" />
                    </span>
                  ) : (
                    <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/30" />
                  )}
                </span>
                <span
                  className={`flex-1 truncate ${
                    isActive ? "text-foreground font-medium" : done ? "text-muted-foreground" : "text-muted-foreground/60"
                  }`}
                >
                  {s.label}
                </span>
              </div>
            );
          })}
        </div>

        {/* ── Footer ────────────────────────────────────────────────── */}
        <div className="mt-auto flex items-center justify-end px-4 py-3">
          <Badge className="text-[9px] font-semibold uppercase tracking-widest rounded-full px-2.5 h-5 border bg-primary/10 text-primary border-primary/20">
            Building
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Failed card ──────────────────────────────────────────────────────────────

/**
 * Failed Build-with-AI offering: plain failed treatment (persona failed-card
 * language) with Retry (re-runs from the persisted build input) and Discard
 * (hard delete — failed drafts have nothing referencing them yet).
 */
export function OfferingBuildFailedCard({ offer, brandId, index }: {
  offer: Offer;
  brandId: string;
  index: number;
}) {
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/offers`] });

  const retryMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/offers/${offer.id}/build-retry`);
    },
    onSuccess: () => invalidate(),
    onError: (e: any) => {
      toast({ title: "Could not retry", description: e?.message || "Please try again.", variant: "destructive" });
    },
  });

  const discardMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", `/api/offers/${offer.id}`);
    },
    onSuccess: () => {
      invalidate();
      toast({ title: "Draft discarded", description: "The failed offering draft was removed." });
    },
    onError: (e: any) => {
      setConfirmDiscard(false);
      toast({ title: "Could not discard", description: e?.message || "Please try again.", variant: "destructive" });
    },
  });

  return (
    <>
      <Card
        className={`persona-card relative overflow-hidden rounded-2xl h-full ${CARD_MIN_H} border-destructive/30`}
        style={{ animationDelay: `${index * 80}ms` }}
        data-testid={`card-offer-failed-${offer.id}`}
      >
        <CardContent className="p-0 flex flex-col h-full">
          <div
            className="relative overflow-hidden h-[200px] md:h-[210px] flex-shrink-0 flex flex-col justify-center px-5 py-5"
            style={{ backgroundColor: BAND }}
          >
            <div className="absolute inset-y-0 right-0 w-[45%] bg-gradient-to-l from-destructive/15 to-transparent flex items-center justify-center pointer-events-none">
              <Tag className="h-12 w-12 text-white/10" />
            </div>
            <div className="relative z-10 max-w-[60%] flex flex-col gap-2.5 overflow-hidden">
              <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-white/10 border border-white/15 px-2.5 py-0.5 text-[8.5px] font-semibold uppercase tracking-[0.18em] text-white/70">
                <Tag className="h-3 w-3" />Offering
              </span>
              <h3
                className={`${PERSONA_NAME_TIER_COMPACT[personaNameTier(offer.name)]} text-[#f3ede3]`}
                style={SERIF}
                data-testid={`text-name-${offer.id}`}
              >
                {offer.name}
              </h3>
              <p className="text-[12px] leading-[18px] flex items-center gap-1.5 text-red-300/80">
                <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />Generation failed
              </p>
              {offer.buildError && (
                <p className="text-[11.5px] text-white/45 leading-[17px] h-[34px] line-clamp-2 italic" data-testid={`text-build-error-${offer.id}`}>
                  {offer.buildError}
                </p>
              )}
            </div>
          </div>
          <div className="px-5 pt-4 text-[12px] leading-[18px] text-muted-foreground">
            The AI build for this offering didn't finish. You can retry the generation with the same
            description and answers, or discard the draft.
          </div>
          <div className="mt-auto flex items-center justify-between gap-2 px-4 py-3">
            <div className="flex items-center gap-1.5">
              <Button
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 rounded-lg px-3 text-[12px]"
                disabled={retryMutation.isPending || discardMutation.isPending}
                onClick={() => retryMutation.mutate()}
                data-testid={`button-retry-offer-build-${offer.id}`}
              >
                {retryMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                Retry
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 gap-1.5 rounded-lg px-2.5 text-[12px] text-destructive hover:text-destructive hover:bg-destructive/10"
                disabled={retryMutation.isPending || discardMutation.isPending}
                onClick={() => setConfirmDiscard(true)}
                data-testid={`button-discard-offer-build-${offer.id}`}
              >
                {discardMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                Discard
              </Button>
            </div>
            <Badge className="text-[9px] font-semibold uppercase tracking-widest rounded-full px-2.5 h-5 border bg-destructive/10 text-destructive border-destructive/20">
              Failed
            </Badge>
          </div>
        </CardContent>
      </Card>
      <AlertDialog open={confirmDiscard} onOpenChange={setConfirmDiscard}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard this failed offering draft?</AlertDialogTitle>
            <AlertDialogDescription>
              "{offer.name}" failed to generate and will be permanently removed. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-discard-offer-build">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => discardMutation.mutate()}
              data-testid="button-confirm-discard-offer-build"
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
